/**
 * useModeToggle — drives the header's Player/TD mode badge.
 *
 * "Mode" is never stored as its own flag — it's derived fresh from the
 * current route on every render (`isTdModePath`), so it can never drift from
 * what the page you're looking at actually is. The one bit of real state is
 * where you last were in the *other* mode, so tapping the badge snaps back
 * to it rather than jumping to some computed "equivalent" page (which often
 * doesn't exist — there's no player-facing version of the TD Players tab).
 * That state is in-memory only and resets each session on purpose: a TD
 * toggling back weeks later shouldn't land on a since-finished tournament's
 * management page.
 *
 * The remembered path is only a fallback, though. When the current page is
 * *about* a specific tournament, the toggle crosses over to that same
 * tournament rather than to wherever the user happened to be last. Without
 * that, a TD who ran an event entirely from `/td` (and refereed from there)
 * had never visited a player path, so switching to player mode dropped them on
 * `/` — reading, at the end of a tournament, as being ejected from the event
 * they were just running.
 *
 * The player side of that crossing needs a tournament id, and the canonical
 * player URL is slug-based (`/t/:slug`). It used to be reached by way of
 * `/tournament/:id/...` tab routes, which carried the id in the path; those are
 * anchors on the hub now, so a playing TD sits on `/t/:slug` for the whole
 * event. The id is resolved from the hub's own cached query instead — read,
 * never fetched, since the hub page owns that fetch.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery, skipToken } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';

const DEFAULT_PLAYER_PATH = '/';
const DEFAULT_TD_PATH = '/td';

/** `/td` and `/td/...` are TD mode; everything else (including `/t/:slug`) is player mode. */
export function isTdModePath(pathname: string): boolean {
  return pathname === '/td' || pathname.startsWith('/td/');
}

const TD_TOURNAMENT_RE = /^\/td\/tournament\/([^/]+)/;
const PLAYER_TOURNAMENT_RE = /^\/tournament\/([^/]+)/;
const HUB_SLUG_RE = /^\/t\/([^/]+)/;

/** The slug of the tournament hub the path points at, if it is one. */
export function hubSlugFromPath(pathname: string): string | null {
  return pathname.match(HUB_SLUG_RE)?.[1] ?? null;
}

/**
 * The id of the tournament the given path is about, if any. `/td/tournament/new`
 * is the create form, not a tournament, so it deliberately yields nothing.
 */
export function tournamentIdFromPath(pathname: string): string | null {
  const id = (pathname.match(TD_TOURNAMENT_RE) ?? pathname.match(PLAYER_TOURNAMENT_RE))?.[1];
  return !id || id === 'new' ? null : id;
}

/**
 * Where tapping the mode badge should go. Prefers the other mode's view of the
 * tournament currently on screen; falls back to wherever the user last was in
 * that mode. `/tournament/:id` resolves the slug and redirects to `/t/:slug`,
 * so it works as the player-side target without needing the slug here.
 *
 * @param hubTournamentId the id behind a `/t/:slug` hub, when it's known —
 *   without it a playing TD toggling from the hub lands on whatever TD page
 *   they last saw (or `/td`), which after the tab routes became hub anchors is
 *   every time they toggle.
 */
export function resolveToggleTarget(
  pathname: string,
  isTdMode: boolean,
  fallbacks: { lastPlayerPath: string; lastTdPath: string },
  hubTournamentId?: string | null
): string {
  const tournamentId = tournamentIdFromPath(pathname) ?? (isTdMode ? null : hubTournamentId);
  if (tournamentId) {
    return isTdMode ? `/tournament/${tournamentId}` : `/td/tournament/${tournamentId}`;
  }
  return isTdMode ? fallbacks.lastPlayerPath : fallbacks.lastTdPath;
}

export interface UseModeToggleResult {
  /** Whether the current route is inside TD-mode chrome. */
  isTdMode: boolean;
  /** Navigate to wherever the user last was in the other mode. */
  toggleMode: () => void;
}

export function useModeToggle(): UseModeToggleResult {
  const location = useLocation();
  const navigate = useNavigate();
  const lastPlayerPath = useRef(DEFAULT_PLAYER_PATH);
  const lastTdPath = useRef(DEFAULT_TD_PATH);

  const isTdMode = isTdModePath(location.pathname);

  // On the hub we have a slug, not an id. Subscribe (without fetching — the hub
  // page owns that) to its cached data so the toggle can cross to this
  // tournament's TD page, updating reactively once the hub data lands.
  const hubSlug = hubSlugFromPath(location.pathname);
  const { data: hubData } = useQuery<{ publicData?: { tournament?: { id?: string } } }>({
    queryKey: queryKeys.tournamentSummary(hubSlug ?? ''),
    queryFn: skipToken,
  });
  const hubTournamentId = hubSlug ? hubData?.publicData?.tournament?.id ?? null : null;

  useEffect(() => {
    const path = location.pathname + location.search;
    if (isTdMode) {
      lastTdPath.current = path;
    } else {
      lastPlayerPath.current = path;
    }
  }, [location.pathname, location.search, isTdMode]);

  const toggleMode = () => {
    navigate(
      resolveToggleTarget(
        location.pathname,
        isTdMode,
        {
          lastPlayerPath: lastPlayerPath.current,
          lastTdPath: lastTdPath.current,
        },
        hubTournamentId
      )
    );
  };

  return { isTdMode, toggleMode };
}

export default useModeToggle;
